import { useEffect, useState } from 'react'
import { parseCsv } from '../utils/csv'
import {
  findFeatureOverrides,
  normalizeKey,
  tierDisplayNames,
} from '../data/pricing'

const PRICING_CSV_URL =
  'https://cdn.magicpatterns.com/uploads/dtGNjDw6Vu9o9X9N7y8nM3/pricing.csv'

// Columns that describe the feature rather than a license value
const FEATURE_COLUMN = 'feature'
const DESCRIPTION_COLUMN = 'description'
const CATEGORY_COLUMN = 'category'

export interface PricingFeature {
  feature: string
  description: string
  values: string[]
}

export interface PricingGroup {
  category: string
  features: PricingFeature[]
}

export interface PricingData {
  tiers: string[]
  groups: PricingGroup[]
  priceRow: PricingFeature | null
}

export type PricingStatus = 'loading' | 'ready' | 'error'

export function usePricingData() {
  const [data, setData] = useState<PricingData | null>(null)
  const [status, setStatus] = useState<PricingStatus>('loading')

  useEffect(() => {
    let cancelled = false

    fetch(PRICING_CSV_URL)
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load pricing')
        return response.text()
      })
      .then((text) => {
        if (cancelled) return
        const { headers, rows } = parseCsv(text)
        const normalized = headers.map((header) => header.toLowerCase())

        const featureIndex = Math.max(normalized.indexOf(FEATURE_COLUMN), 0)
        const descriptionIndex = normalized.indexOf(DESCRIPTION_COLUMN)
        const categoryIndex = normalized.indexOf(CATEGORY_COLUMN)

        // Everything that isn't a descriptive column is a license column
        const tierIndexes = headers
          .map((header, index) => ({
            header: tierDisplayNames[normalizeKey(header)] ?? header,
            index,
          }))
          .filter(
            ({ header, index }) =>
              header !== '' &&
              index !== featureIndex &&
              index !== descriptionIndex &&
              index !== categoryIndex,
          )
        const tiers = tierIndexes.map(({ header }) => header)

        const cell = (cells: string[], index: number) =>
          index >= 0 ? (cells[index] ?? '').trim() : ''

        const allRows = rows
          .map((cells) => {
            const feature = cell(cells, featureIndex)
            const overrides = findFeatureOverrides(feature)
            return {
              feature,
              description: cell(cells, descriptionIndex),
              category: cell(cells, categoryIndex),
              values: tierIndexes.map(({ header, index }) => {
                const override = overrides?.[normalizeKey(header)]
                return override ?? cell(cells, index)
              }),
            }
          })
          .filter((row) => row.feature !== '')

        const priceIndex = allRows.findIndex((row) =>
          /^(price|pricing|cost|list price|msrp|rrp)\b/i.test(row.feature),
        )
        const priceRow = priceIndex >= 0 ? allRows[priceIndex] : null
        const featureRows =
          priceIndex >= 0
            ? allRows.filter((_, index) => index !== priceIndex)
            : allRows

        // Group by category, preserving the order they appear in the sheet
        const groups: PricingGroup[] = []
        featureRows.forEach((row) => {
          const existing = groups.find((group) => group.category === row.category)
          const feature: PricingFeature = {
            feature: row.feature,
            description: row.description,
            values: row.values,
          }
          if (existing) {
            existing.features.push(feature)
          } else {
            groups.push({
              category: row.category,
              features: [feature],
            })
          }
        })

        setData({
          tiers,
          groups,
          priceRow,
        })
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })

    return () => {
      cancelled = true
    }
  }, [])

  return {
    data,
    status,
  }
}
